import { ENTER } from '@angular/cdk/keycodes';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChild,
  ViewChildren
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ApolloQueryResult } from '@apollo/client';
import { QueryRef } from 'apollo-angular';
import { takeUntil } from 'rxjs/operators';

import {
  CampaignStatusType,
  SearchCampaigns, SearchCampaignsGQL, SearchCampaignsQuery, SearchCampaignsQueryVariables
} from 'generated/graphql';

import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import {
  InfiniteSearchScrollableComponentTemplate, InfiniteSearchScrollAdaptor
} from 'shared/InfiniteSearchScrollAdaptor';

import { ListItemComponent } from '../list-item/list-item.component';

@Component({
  selector: 'app-campaign-quicksearch',
  templateUrl: './campaign-quicksearch.component.html',
})
export class CampaignQuickSearchComponent extends WithDisposableSubject()
  implements OnInit, AfterViewInit, InfiniteSearchScrollableComponentTemplate<SearchCampaigns.Node> {
  readonly itemsPerPage = 50;
  @ViewChildren(ListItemComponent) items: QueryList<ListItemComponent>;
  @Output() itemSelected = new EventEmitter<any>();

  /** optional filter function to filter visible results */
  @Input() resultsFilterFn: (node: SearchCampaigns.Node) => boolean;

  filterInput = new UntypedFormControl();

  @ViewChild('scrollContainer', { static: true })
  private _scrollContainer: CdkVirtualScrollViewport;

  loading = false;
  resultsQuery: QueryRef<SearchCampaignsQuery, SearchCampaignsQueryVariables>;
  results: SearchCampaigns.Node[] = [];

  private _infiniteSearchScrollAdaptor: InfiniteSearchScrollAdaptor<any>;
  /**
   *
   */
  constructor(
    private cd: ChangeDetectorRef,
    private searchCampaigns: SearchCampaignsGQL
  ) {
    super();
  }

  getScrollContainer() {
    return this._scrollContainer;
  }

  getChangeDetectorRef() {
    return this.cd;
  }

  getFetchMoreVariables(page: number) {
    const skipSize = page * this.itemsPerPage;
    return {
      withEntitiesCount: false,
      keyword: this.filterInput.value,
      first: this.itemsPerPage,
      status: CampaignStatusType.Active,
      after: page > 0 ? btoa(skipSize.toString()).toString() : null
    };
  }

  // **Life Cycle Hooks** //
  ngOnInit() {
    this.resultsQuery = this.searchCampaigns.watch(this.getFetchMoreVariables(0));

    this._infiniteSearchScrollAdaptor = new InfiniteSearchScrollAdaptor(this);

    this._infiniteSearchScrollAdaptor.results.pipe(
      takeUntil(this.disposer)
    ).subscribe(res => {
      if (this.resultsFilterFn) {
        this.results = res.filter(r => this.resultsFilterFn(r));
      } else {
        this.results = res;
      }
      this.cd.markForCheck();
    });
  }

  ngAfterViewInit() {
    this._infiniteSearchScrollAdaptor.connect();
  }

  focusResults() {
    this._infiniteSearchScrollAdaptor.keyManager.setFirstItemActive();
  }

  selectResults(res: ApolloQueryResult<SearchCampaignsQuery>) {
    return res.data.searchCampaigns.edges.map(n => n.node);
  }

  /**
   * Triggered when item is selected
   *
   * @param item Selected item
   */
  itemClicked(item: any) {
    this.itemSelected.emit(item);
  }

  /**
   * Keyboard actions for the results list
   *
   * @param event keyboard event
   */
  onResultsListKeydown(event) {
    if (event.keyCode === ENTER) {
      const item = this._infiniteSearchScrollAdaptor.keyManager.activeItem.item;
      if (item) {
        this.itemClicked(item);
      }
    } else {
      this._infiniteSearchScrollAdaptor.keyManager.onKeydown(event);
    }
  }
}
